import { OVERLAY_MAX_LAYERS } from "../../api/navigation.generated";
import type { OverlayUrlParam } from "../../navigation/overlayUrlParams";

/**
 * A layer as a URL, a deep link or Marvin's page context names it: the sheet's own file and, when
 * known, the section group that pins its exact revision (a file shared by several revisions through
 * content-hash deduplication cannot say which one was meant).
 */
export interface OverlayLayerRef {
    fileId: string;
    sectionGroupId: string | null;
    /** The sheet's own document, when the opener knows it (a shop-vs-contract stack); never in a URL. */
    documentId?: string | null;
}

/** A layer on screen, as the viewer reports the stack: the ref plus its picker label
 *  (`code - title`; null until a URL-restored layer is resolved). */
export interface OverlayStackEntry extends OverlayLayerRef {
    label: string | null;
}

export const NO_OVERLAY_LAYERS: readonly OverlayStackEntry[] = [];

/** The `?overlayLayer=<fileId>[:<sectionGroupId>]` entries, one per layer, bottom first. */
export const OVERLAY_LAYER_PARAM: OverlayUrlParam = "overlayLayer";
/** Single-overlay links in tickets and emails: `?overlaySheet=<fileId>[&overlaySectionGroup=<id>]`. */
const LEGACY_SHEET_PARAM: OverlayUrlParam = "overlaySheet";
const LEGACY_SECTION_GROUP_PARAM: OverlayUrlParam = "overlaySectionGroup";

const UUID = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

export function formatOverlayLayerParam(layer: OverlayLayerRef): string {
    return layer.sectionGroupId ? `${layer.fileId}:${layer.sectionGroupId}` : layer.fileId;
}

function parseOverlayLayerParam(raw: string): OverlayLayerRef | null {
    const separator = raw.indexOf(":");
    const fileId = separator === -1 ? raw : raw.slice(0, separator);
    const sectionGroupId = separator === -1 ? null : raw.slice(separator + 1);
    if (!fileId) return null;
    // An explicit revision that does not parse must not silently become a file-only lookup.
    if (sectionGroupId !== null && !UUID.test(sectionGroupId)) return null;
    return { fileId, sectionGroupId };
}

/**
 * The layers a URL names, bottom first; empty when it names none or any entry is malformed,
 * repeated or past the stack's cap (a hand-edited link is refused whole, never trimmed).
 * A legacy `?overlaySheet=` link is read as a one-layer list.
 */
export function parseOverlayLayers(
    searchParams: Pick<URLSearchParams, "get" | "getAll">,
): OverlayLayerRef[] {
    const entries = searchParams.getAll(OVERLAY_LAYER_PARAM);
    if (entries.length === 0) {
        const fileId = searchParams.get(LEGACY_SHEET_PARAM);
        if (!fileId) return [];
        const sectionGroupId = searchParams.get(LEGACY_SECTION_GROUP_PARAM) || null;
        return sectionGroupId && !UUID.test(sectionGroupId) ? [] : [{ fileId, sectionGroupId }];
    }
    if (entries.length > OVERLAY_MAX_LAYERS) return [];
    const layers: OverlayLayerRef[] = [];
    for (const entry of entries) {
        const layer = parseOverlayLayerParam(entry);
        if (!layer || layers.some((seen) => seen.fileId === layer.fileId)) return [];
        layers.push(layer);
    }
    return layers;
}

/** Sets the layer entries on `params` and drops the legacy single-overlay spelling. */
export function setOverlayLayerParams(
    params: URLSearchParams,
    layers: readonly OverlayLayerRef[],
): void {
    params.delete(LEGACY_SHEET_PARAM);
    params.delete(LEGACY_SECTION_GROUP_PARAM);
    params.delete(OVERLAY_LAYER_PARAM);
    for (const layer of layers) params.append(OVERLAY_LAYER_PARAM, formatOverlayLayerParam(layer));
}
